import { useMemo, useRef } from 'react'
import ReactMarkdown from 'react-markdown'
import rehypeHighlight from 'rehype-highlight'
import remarkMath from 'remark-math'
import remarkGfm from 'remark-gfm'
import rehypeKatex from 'rehype-katex'
import { toPng } from 'html-to-image'
import type { Components } from 'react-markdown'
import type { SessionMessage, StreamingMessage } from '../types'
import { MermaidBlock } from './MermaidBlock'
import { ExerciseBlock } from './ExerciseBlock'
import { ExerciseSetProvider } from './ExerciseSetProvider'
import { ChartBlock } from './ChartBlock'
import { useTranslation } from 'react-i18next'

interface ChatBubbleProps {
  message: SessionMessage | StreamingMessage
  onSendMessage?: (content: string) => void
}

function isStreamingMessage(msg: SessionMessage | StreamingMessage): msg is StreamingMessage {
  return 'isStreaming' in msg
}

/**
 * Convert LaTeX-style math delimiters (\(...\) and \[...\]) to KaTeX-compatible
 * dollar-sign delimiters ($...$ and $$...$$), which remark-math v6 recognises.
 */
function normalizeMathDelimiters(content: string): string {
  return content
    .replaceAll(String.raw`\[`, '$$')
    .replaceAll(String.raw`\]`, '$$')
    .replaceAll(String.raw`\(`, '$')
    .replaceAll(String.raw`\)`, '$')
}

function extractCodeString(children: React.ReactNode): string {
  if (typeof children === 'string') return children
  if (Array.isArray(children)) {
    return (children as React.ReactNode[]).map((c) => (typeof c === 'string' ? c : '')).join('')
  }
  return ''
}

/**
 * Attribue à chaque bloc ```exercise sa position dans la série (DEFAUTS-EXERCICES.md,
 * D1) : ReactMarkdown appelle `code()` dans l'ordre d'apparition du document pendant
 * le rendu, donc un simple compteur fermé sur cet appel de `buildMarkdownComponents`
 * (une fabrique appelée à chaque rendu de `ChatBubble`, jamais un ref) suffit à
 * numéroter les exercices de façon stable, sans dépendre d'un ordre de montage d'effets.
 */
function buildMarkdownComponents(isStreaming = false): Components {
  let nextExerciseIndex = 0

  return {
    code({ className, children, ...props }) {
      const lang = /language-(\w+)/.exec(className ?? '')?.[1] ?? ''
      const codeStr = extractCodeString(children).replace(/\n$/, '')

      if (lang === 'mermaid') {
        return <MermaidBlock code={codeStr} isStreaming={isStreaming} />
      }

      if (lang === 'exercise') {
        const slotIndex = nextExerciseIndex
        nextExerciseIndex += 1
        return <ExerciseBlock raw={codeStr} slotIndex={slotIndex} />
      }

      if (lang === 'chart') {
        return <ChartBlock raw={codeStr} isStreaming={isStreaming} />
      }

      return (
        <code className={className} {...props}>
          {children}
        </code>
      )
    },
  }
}

interface ExportButtonProps {
  contentRef: React.RefObject<HTMLDivElement | null>
}

function ExportButton({ contentRef }: Readonly<ExportButtonProps>) {
  const { t } = useTranslation()

  const handleExport = async () => {
    if (!contentRef.current) return
    try {
      const dataUrl = await toPng(contentRef.current, {
        backgroundColor: '#ffffff',
        pixelRatio: 2,
      })
      const link = document.createElement('a')
      link.download = `fiche-revision-${Date.now()}.png`
      link.href = dataUrl
      link.click()
    } catch {
      // silent – export not critical
    }
  }

  return (
    <button
      onClick={handleExport}
      title={t('session.download_revision_sheet')}
      className="inline-flex items-center gap-1 text-[10px] text-gray-400 hover:text-[var(--color-primary)] transition-colors"
      type="button"
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3 h-3">
        <path d="M8.75 2.75a.75.75 0 0 0-1.5 0v5.69L5.03 6.22a.75.75 0 0 0-1.06 1.06l3.5 3.5a.75.75 0 0 0 1.06 0l3.5-3.5a.75.75 0 0 0-1.06-1.06L8.75 8.44V2.75Z" />
        <path d="M3.5 9.75a.75.75 0 0 0-1.5 0v1.5A2.75 2.75 0 0 0 4.75 14h6.5A2.75 2.75 0 0 0 14 11.25v-1.5a.75.75 0 0 0-1.5 0v1.5c0 .69-.56 1.25-1.25 1.25h-6.5c-.69 0-1.25-.56-1.25-1.25v-1.5Z" />
      </svg>
      {t('session.revision_sheet_short')}
    </button>
  )
}

export function ChatBubble({ message, onSendMessage }: Readonly<ChatBubbleProps>) {
  const isUser = message.role === 'user'
  const streaming = isStreamingMessage(message)
  const contentRef = useRef<HTMLDivElement>(null)
  // react-markdown recrée tout son arbre — donc démonte/remonte les exercices en
  // cours de réponse — chaque fois que la prop `components` change d'identité. Sans
  // ce useMemo, un ré-affichage de ChatBubble sans rapport avec ce message (ex. la
  // liste des messages qui se recharge après la fin d'un streaming ailleurs)
  // reconstruit l'objet à chaque rendu et efface les réponses déjà saisies.
  //
  // Une mémoïsation manuelle dans une ref mutée pendant le rendu serait plus
  // robuste que `useMemo` (dont React ne garantit pas la conservation du cache),
  // mais la règle ESLint `react-hooks/refs` du projet l'interdit explicitement
  // (lire/écrire un ref pendant le rendu) : `useMemo` reste donc le mécanisme
  // disponible ici, avec des dépendances qui couvrent exactement les deux cas où
  // l'identité doit changer.
  const markdownComponents = useMemo(
    () => buildMarkdownComponents(streaming),
    // message.content ne sert pas au calcul lui-même : il force une nouvelle
    // fabrique (donc un compteur d'exercices remis à zéro) exactement quand le
    // contenu affiché change réellement, ni plus ni moins — voir le commentaire
    // ci-dessus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [streaming, message.content],
  )

  const timestamp = !streaming && message.createdAt
    ? (() => {
      const d = new Date(message.createdAt)
      return Number.isNaN(d.getTime())
        ? null
        : d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    })()
    : null

  return (
    <div className={`flex items-end gap-2.5 px-4 py-1.5 ${isUser ? 'flex-row-reverse' : ''}`}>
      {/* Avatar */}
      {isUser ? (
        <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center shrink-0">
          <span className="text-white text-xs font-bold">M</span>
        </div>
      ) : (
        <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
          <span className="material-symbols-outlined text-primary" style={{ fontSize: '16px' }}>smart_toy</span>
        </div>
      )}

      {/* Bubble */}
      <div className={`min-w-0 flex flex-col gap-0.5 ${isUser ? 'max-w-[85%] sm:max-w-[70%] items-end' : 'max-w-[92%] sm:max-w-[75%] items-start'}`}>
        <div
          ref={contentRef}
          className={`relative w-full rounded-2xl px-4 py-3 text-sm leading-relaxed break-words overflow-hidden ${isUser
            ? 'rounded-tr-none bg-primary text-white'
            : 'rounded-tl-none bg-surface-container text-on-surface'
            }`}
        >
          {isUser ? (
            <p className="whitespace-pre-wrap">{message.content}</p>
          ) : (
            <ExerciseSetProvider
              isStreaming={streaming}
              sessionId={!streaming ? message.sessionId : null}
              onSendMessage={onSendMessage}
            >
              <div className="chat-content">
                <ReactMarkdown
                  remarkPlugins={[remarkMath, remarkGfm]}
                  rehypePlugins={[rehypeHighlight, rehypeKatex]}
                  components={markdownComponents}
                >
                  {normalizeMathDelimiters(message.content || (streaming ? ' ' : ''))}
                </ReactMarkdown>
              </div>
              {streaming && message.isStreaming && (
                <span className="inline-block w-0.5 h-[1em] bg-primary animate-pulse align-middle ml-0.5" />
              )}
            </ExerciseSetProvider>
          )}
        </div>

        {/* Meta row: timestamp + export */}
        <div className={`flex items-center gap-2 px-1 ${isUser ? 'flex-row-reverse' : ''}`}>
          {timestamp && (
            <span className="text-[10px] text-gray-400">{timestamp}</span>
          )}
          {!isUser && !streaming && message.content && message.content.length > 60 && (
            <ExportButton contentRef={contentRef} />
          )}
        </div>
      </div>
    </div>
  )
}
